import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Select,
  Input,
  Button,
  Table,
  Progress,
  Typography,
  Badge,
  Flex,
  Space,
  Tooltip,
  Modal,
  Tabs,
  Form,
  Tag,
  Popconfirm
} from 'antd';
import BatteryGauge from 'react-battery-gauge';
import {
  apiDeleteDevice,
  apiGetDevicesInformation,
  apiGetDevicesInformationV2,
  apiUpdateDeviceStatus,
  apiUpdateDeviceCondition
} from '@services/ManageDevices/DeviceServices';
import { showError, showSuccess, showWarning } from '@components/notification';
import { communityList, downloadBlobFile } from '@utils/utils';
import StatCards from './StatCards';
import {
  SearchOutlined,
  ClearOutlined,
  DownloadOutlined,
  PlusOutlined,
  UploadOutlined,
  EditFilled,
  DeleteFilled,
  ToolFilled,
  EyeFilled,
  CheckCircleFilled,
  CloseCircleFilled,
  RollbackOutlined,
  SyncOutlined,
  StopOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ModalImportFile from './ModalImportFile';
import { URL_API } from '@configs/index';
import TabPane from 'antd/es/tabs/TabPane';
import { usePermission } from '@contexts/Auth/UsePermission';

const { Option } = Select;
const { Title, Paragraph } = Typography;

const DeviceDetailRow = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      gap: 16,
      padding: '12px 0',
      borderBottom: '1px solid #f0f0f0'
    }}>
    <Typography.Text type="secondary" style={{ width: 170, flexShrink: 0 }}>
      {label}
    </Typography.Text>
    <Typography.Text style={{ flex: 1, fontWeight: 500, overflowWrap: 'anywhere' }}>
      {value || '—'}
    </Typography.Text>
  </div>
);

export default function List() {
  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [deviceList, setDeviceList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [flagSearch, setFlagSearch] = useState(false);
  const [identifierSearch, setIdentifierSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();
  const [visibleModalImport, setVisibleModalImport] = useState(false);
  const [custId, setCustId] = useState(null);
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [stateFilter, setStateFilter] = useState(null);
  const { device } = usePermission();

  // Device information modal state
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  // Device condition modal state
  const [conditionModalVisible, setConditionModalVisible] = useState(false);
  const [conditionRecord, setConditionRecord] = useState(null);
  const [conditionCode, setConditionCode] = useState(null);
  const [conditionName, setConditionName] = useState('');
  const [conditionNote, setConditionNote] = useState('');
  const [isSavingCondition, setIsSavingCondition] = useState(false);

  // Inline note edit state
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteValue, setEditingNoteValue] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  const handleStartEditNote = (record) => {
    setEditingNoteId(record.id);
    setEditingNoteValue(record.state_note || '');
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteValue('');
  };

  const handleSaveNote = async (record) => {
    try {
      setIsSavingNote(true);
      await apiUpdateDeviceCondition(record.id, {
        state_code: record.state_code || 'NORMAL',
        state_name: record.state_name || null,
        note: editingNoteValue.trim() || null
      });
      showSuccess({ message: 'Cập nhật ghi chú thành công!' });
      setEditingNoteId(null);
      fetchData();
    } catch (error) {
      showError(error?.response?.data?.message || 'Có lỗi xảy ra!');
    } finally {
      setIsSavingNote(false);
    }
  };

  const DEVICE_CONDITIONS = [
    { code: 'NORMAL', name: 'Bình thường', color: '#10b981' },
    { code: 'LOST', name: 'Thất lạc', color: '#e11d48' },
    { code: 'DAMAGED', name: 'Hư hỏng', color: '#ea580c' },
    { code: 'OTHER', name: 'Khác', color: '#8c8c8c' }
  ];

  const getConditionConfig = (code) => DEVICE_CONDITIONS.find((c) => c.code === code) || null;

  const handleOpenConditionModal = (record) => {
    setConditionRecord(record);
    setConditionCode(record.state_code || null);
    setConditionName(record.state_name || '');
    setConditionNote(record.state_note || '');
    setConditionModalVisible(true);
  };

  const handleSaveCondition = async () => {
    if (!conditionCode) {
      showError('Vui lòng chọn loại tình trạng thiết bị!');
      return;
    }
    if (conditionCode === 'OTHER' && !conditionName.trim()) {
      showError('Vui lòng nhập tên tình trạng thiết bị!');
      return;
    }
    try {
      setIsSavingCondition(true);
      const selectedCondition = getConditionConfig(conditionCode);
      const nameToSave =
        conditionCode === 'OTHER'
          ? conditionName.trim()
          : selectedCondition?.name || conditionName.trim();
      await apiUpdateDeviceCondition(conditionRecord.id, {
        state_code: conditionCode,
        state_name: nameToSave,
        note: conditionNote.trim() || null
      });
      showSuccess({ message: 'Cập nhật tình trạng thiết bị thành công!' });
      setConditionModalVisible(false);
      fetchData();
    } catch (error) {
      showError(error?.response?.data?.message || 'Có lỗi xảy ra!');
    } finally {
      setIsSavingCondition(false);
    }
  };

  const fetchData = async () => {
    try {
      setIsFetching(true);
      const res = await apiGetDevicesInformationV2({
        page: currentPage,
        size: pageSize,
        identifierKeyword: identifierSearch.trim().toLowerCase() || undefined,
        modelKeyword: modelSearch.trim().toLowerCase() || undefined,
        customerId: custId ?? undefined,
        status: status === 'all' ? undefined : status,
        type: type || undefined,
        stateCode: stateFilter || undefined,
        isGetInManageDevice: true
      });
      setDeviceList(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError('Something went wrong!');
      console.log(err);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch, selectedCommunity, status, type, stateFilter]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleEdit = (record) => {
    navigate(`/device/${record.id}`);
  };

  const handleReplacement = (record) => {
    navigate(`/device/replace/${record.id}`);
  };
  const handleViewDetail = (record) => {
    setDetailRecord(record);
    setDetailModalVisible(true);
  };

  const getDeviceStatusConfig = (deviceStatus) => {
    switch (deviceStatus) {
      case 'IN_USE':
        return { icon: <CheckCircleFilled />, text: 'Đang sử dụng', color: '#10b981' };
      case 'UNDER_WARRANTY':
        return { icon: <ToolFilled />, text: 'Bảo hành', color: '#f59e0b' };
      case 'UNUSABLE':
        return { icon: <StopOutlined />, text: 'Không dùng được', color: '#e11d48' };
      default:
        return { icon: <CloseCircleFilled />, text: 'Lưu kho', color: '#5d5d5d' };
    }
  };

  const renderDeviceStatus = (deviceStatus) => {
    const config = getDeviceStatusConfig(deviceStatus);
    return (
      <Space size={8} style={{ color: config.color }}>
        {React.cloneElement(config.icon, { style: { fontSize: 17 } })}
        <span style={{ fontWeight: 600 }}>{config.text}</span>
      </Space>
    );
  };

  const renderDeviceCondition = (record) => {
    const code = record?.state_code || 'NORMAL';
    const config = getConditionConfig(code) || DEVICE_CONDITIONS[0];
    const icon =
      code === 'NORMAL' ? (
        <CheckCircleFilled />
      ) : code === 'DAMAGED' ? (
        <ToolFilled />
      ) : (
        <CloseCircleFilled />
      );

    return (
      <Space size={8} style={{ color: config.color }}>
        {React.cloneElement(icon, { style: { fontSize: 17 } })}
        <span style={{ fontWeight: 600 }}>{record?.state_name || config.name}</span>
      </Space>
    );
  };

  const handleWarranty = (record) => {
    Modal.confirm({
      title: 'Chuyển thiết bị sang trạng thái bảo hành?',
      content: `Thiết bị ${record.serial_no} sẽ được chuyển từ Lưu kho sang Bảo hành`,
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await apiUpdateDeviceStatus(record?.id, { status: 'BH' });
          showSuccess({ message: 'Đã chuyển thiết bị sang trạng thái bảo hành!' });
          setStatus('UNDER_WARRANTY');
          setCurrentPage(1);
        } catch (error) {
          showError(error?.response?.data?.message || 'Có lỗi xảy ra!');
        }
      }
    });
  };

  const handleReturnToStorage = (record) => {
    Modal.confirm({
      title: 'Chuyển thiết bị về lưu kho?',
      content: `Thiết bị ${record.serial_no} sẽ được chuyển từ Bảo hành về Lưu kho`,
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await apiUpdateDeviceStatus(record?.id, { status: 'LK' });
          showSuccess({ message: 'Đã chuyển thiết bị về lưu kho!' });
          setStatus('STORED');
          setCurrentPage(1);
          fetchData();
        } catch (error) {
          showError(error?.response?.data?.message || 'Có lỗi xảy ra!');
        }
      }
    });
  };

  const handleMarkUnusable = (record) => {
    Modal.confirm({
      title: 'Chuyển thiết bị sang Không dùng được?',
      content: `Thiết bị ${record.serial_no} sẽ được chuyển từ Lưu kho sang Không dùng được (thất lạc, hư hỏng, bị phá hoại,…)`,
      okText: 'Xác nhận',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await apiUpdateDeviceStatus(record?.id, { status: 'KSD' });
          showSuccess({ message: 'Đã chuyển thiết bị sang trạng thái Không dùng được!' });
          setStatus('UNUSABLE');
          setCurrentPage(1);
        } catch (error) {
          showError(error?.response?.data?.message || 'Có lỗi xảy ra!');
        }
      }
    });
  };

  const handleReturnFromUnusable = (record) => {
    Modal.confirm({
      title: 'Chuyển thiết bị về lưu kho?',
      content: `Thiết bị ${record.serial_no} sẽ được chuyển từ Không dùng được về Lưu kho`,
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await apiUpdateDeviceStatus(record?.id, { status: 'LK' });
          showSuccess({ message: 'Đã chuyển thiết bị về lưu kho!' });
          setStatus('STORED');
          setCurrentPage(1);
        } catch (error) {
          showError(error?.response?.data?.message || 'Có lỗi xảy ra!');
        }
      }
    });
  };

  const handleDelete = (record) => {
    const hasActiveSim = Boolean(record.sim_assignment_id);
    const simCode = record.assigned_sim_iccid || record.assigned_sim_imsi;

    Modal.confirm({
      title: hasActiveSim ? 'Thiết bị đang được gán với SIM' : 'Xóa vĩnh viễn thiết bị bảo hành?',
      content: (
        <div>
          <p>
            Thiết bị: <strong>{record.serial_no}</strong>
          </p>
          {hasActiveSim && (
            <p>
              SIM đang gán: <strong>{simCode || 'Không xác định'}</strong>
            </p>
          )}
          <p style={{ color: '#e11d48', fontWeight: 'bold' }}>
            ⚠️ Thiết bị và bản ghi gán SIM sẽ bị xóa vĩnh viễn, không thể khôi phục!
          </p>
          <p>
            {hasActiveSim
              ? 'Bạn có muốn gỡ SIM và xóa thiết bị không?'
              : 'Bạn có chắc chắn muốn tiếp tục?'}
          </p>
        </div>
      ),
      okText: hasActiveSim ? 'Gỡ SIM và xóa' : 'Xác nhận xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await apiDeleteDevice(record?.id, { unassignSim: hasActiveSim });
          showSuccess({
            message: hasActiveSim
              ? 'Gỡ SIM và xóa thiết bị thành công!'
              : 'Xóa vĩnh viễn thiết bị thành công!'
          });
          fetchData();
        } catch (error) {
          showError(error?.message || error?.response?.data?.message || 'Có lỗi xảy ra!');
        }
      },
      onCancel() {
        console.log('Cancel deletion');
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: 'stt',
        width: 80,
        fixed: 'left'
      },
      {
        title: 'Trạng thái',
        dataIndex: 'status',
        fixed: 'left',
        render: (text, record) => {
          const getStatusConfig = (status) => {
            switch (status) {
              case 'IN_USE':
                return {
                  icon: <CheckCircleFilled style={{ fontSize: '16px', color: '#10b981' }} />,
                  text: 'Đang sử dụng',
                  color: '#10b981'
                };
              case 'UNDER_WARRANTY':
                return {
                  icon: <ToolFilled style={{ fontSize: '16px', color: '#f59e0b' }} />,
                  text: 'Bảo hành',
                  color: '#f59e0b'
                };
              case 'UNUSABLE':
                return {
                  icon: <StopOutlined style={{ fontSize: '16px', color: '#e11d48' }} />,
                  text: 'Không dùng được',
                  color: '#e11d48'
                };
              default:
                return {
                  icon: <CloseCircleFilled style={{ fontSize: '16px', color: '#5D5D5D' }} />,
                  text: 'Lưu kho',
                  color: '#5D5D5D'
                };
            }
          };

          const statusConfig = getStatusConfig(record?.status);

          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {statusConfig.icon}
                <Typography.Text
                  style={{
                    fontSize: '12px',
                    color: statusConfig.color
                  }}>
                  {statusConfig.text}
                </Typography.Text>
              </div>
            </>
          );
        },
        width: 140
      },
      {
        title: 'Số serie',
        dataIndex: 'serial_no',
        fixed: 'left',
        width: 100
      },
      {
        title: 'Số IMEI',
        dataIndex: 'imei',
        width: 100
      },
      {
        title: 'Số IMSI',
        dataIndex: 'imsi',
        width: 100
      },
      {
        title: 'Model',
        dataIndex: 'model',
        width: 100
      },
      {
        title: 'Nhà mạng',
        dataIndex: 'operator',
        width: 130,
        render: (value) => value || '—'
      },
      {
        title: 'Tình trạng thiết bị',
        dataIndex: 'state_code',
        width: 150,
        render: (_, record) => {
          const code = record.state_code || 'NORMAL';
          const cfg = getConditionConfig(code);
          return (
            <Space size={4}>
              <Tag color={cfg?.color || '#10b981'} style={{ margin: 0 }}>
                {record.state_name || cfg?.name || code}
              </Tag>
              {device.edit && (
                <Tooltip title="Cập nhật tình trạng">
                  <Button
                    type="text"
                    size="small"
                    shape="circle"
                    icon={<SyncOutlined style={{ color: '#ea580c', fontSize: '14px' }} />}
                    onClick={() => handleOpenConditionModal(record)}
                  />
                </Tooltip>
              )}
            </Space>
          );
        }
      },
      {
        title: 'Ghi chú',
        dataIndex: 'state_note',
        width: 220,
        render: (val, record) => {
          if (!device.edit) {
            return val ? (
              <span style={{ whiteSpace: 'pre-wrap' }}>{val}</span>
            ) : (
              <span style={{ color: '#bfbfbf' }}>—</span>
            );
          }
          if (editingNoteId === record.id) {
            return (
              <Space size={4} align="start">
                <Input.TextArea
                  autoFocus
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  value={editingNoteValue}
                  onChange={(e) => setEditingNoteValue(e.target.value)}
                  maxLength={500}
                  style={{ width: 140 }}
                />
                <Popconfirm
                  title="Lưu ghi chú này?"
                  okText="Lưu"
                  cancelText="Huỷ"
                  onConfirm={() => handleSaveNote(record)}
                  onCancel={handleCancelEditNote}>
                  <Button
                    type="text"
                    size="small"
                    shape="circle"
                    loading={isSavingNote}
                    icon={<CheckOutlined style={{ color: '#10b981', fontSize: '14px' }} />}
                  />
                </Popconfirm>
                <Button
                  type="text"
                  size="small"
                  shape="circle"
                  icon={<CloseOutlined style={{ color: '#e11d48', fontSize: '14px' }} />}
                  onClick={handleCancelEditNote}
                />
              </Space>
            );
          }
          return (
            <Space size={4}>
              <span style={{ whiteSpace: 'pre-wrap' }}>
                {val || <span style={{ color: '#bfbfbf' }}>—</span>}
              </span>
              <Tooltip title="Sửa ghi chú">
                <Button
                  type="text"
                  size="small"
                  shape="circle"
                  icon={<EditOutlined style={{ color: '#0284c7', fontSize: '13px' }} />}
                  onClick={() => handleStartEditNote(record)}
                />
              </Tooltip>
            </Space>
          );
        }
      },
      {
        title: 'Hành động',
        dataIndex: 'actions',
        width: 150,
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            {device.view && (
              <Tooltip title="Xem chi tiết">
                <Button
                  type="text"
                  shape="circle"
                  icon={<EyeFilled style={{ color: '#0284c7', fontSize: '18px' }} />}
                  onClick={() => handleViewDetail(record)}
                />
              </Tooltip>
            )}
            {device.edit && (
              <Tooltip title="Chỉnh sửa">
                <Button
                  type="text"
                  shape="circle"
                  icon={<EditFilled style={{ color: '#0284c7', fontSize: '18px' }} />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
            )}
            {device.edit && record?.status === 'STORED' && (
              <Tooltip title="Chuyển sang bảo hành">
                <Button
                  type="text"
                  shape="circle"
                  icon={<ToolFilled style={{ color: '#0284c7', fontSize: '18px' }} />}
                  onClick={() => handleWarranty(record)}
                />
              </Tooltip>
            )}
            {device.edit && record?.status === 'STORED' && (
              <Tooltip title="Chuyển sang Không dùng được">
                <Button
                  type="text"
                  shape="circle"
                  icon={<StopOutlined style={{ color: '#e11d48', fontSize: '18px' }} />}
                  onClick={() => handleMarkUnusable(record)}
                />
              </Tooltip>
            )}
            {device.edit && record?.status === 'UNDER_WARRANTY' && (
              <Tooltip title="Chuyển về lưu kho">
                <Button
                  type="text"
                  shape="circle"
                  icon={<RollbackOutlined style={{ color: '#0284c7', fontSize: '18px' }} />}
                  onClick={() => handleReturnToStorage(record)}
                />
              </Tooltip>
            )}
            {device.edit && record?.status === 'UNUSABLE' && (
              <Tooltip title="Chuyển về lưu kho">
                <Button
                  type="text"
                  shape="circle"
                  icon={<RollbackOutlined style={{ color: '#0284c7', fontSize: '18px' }} />}
                  onClick={() => handleReturnFromUnusable(record)}
                />
              </Tooltip>
            )}
            {device.delete && record?.status === 'UNDER_WARRANTY' && (
              <Tooltip title="Xoá thiết bị">
                <Button
                  type="danger"
                  shape="circle"
                  icon={<DeleteFilled style={{ color: '#0284c7', fontSize: '18px' }} />}
                  onClick={() => handleDelete(record)}
                />
              </Tooltip>
            )}
          </Space>
        )
      }
    ],
    [
      device,
      editingNoteId,
      editingNoteValue,
      isSavingNote,
      handleStartEditNote,
      handleCancelEditNote,
      handleSaveNote
    ]
  );

  const handleCustIdChange = (e) => {
    setCustId(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };

  const handleResetSearch = () => {
    setIdentifierSearch('');
    setModelSearch('');
    setCurrentPage(1);
    setFlagSearch(!flagSearch);
  };
  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    try {
      setLoading(true);
      await downloadBlobFile(
        `${URL_API}/api/devices/export-report`,
        'Danh sách thiết bị.xlsx',
        {
          identifierKeyword: identifierSearch.trim().toLowerCase() || undefined,
          modelKeyword: modelSearch.trim().toLowerCase() || undefined,
          isGetInManageDevice: true,
          stateCode: stateFilter || undefined,
          status: status === 'all' ? undefined : status,
          type: type || undefined
        },
        'GET'
      );
    } catch (e) {
      showError('Có lỗi xảy ra, vui lòng liên hệ ban quản trị');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDevice = () => {
    navigate('/devices/add');
  };

  return (
    <div className="dashboard">
      <ModalImportFile
        visible={visibleModalImport}
        onCancel={() => {
          setVisibleModalImport(false);
          fetchData();
        }}
      />
      <Modal
        title="Thông tin thiết bị"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Đóng
          </Button>
        ]}
        width={560}
        destroyOnClose>
        <div>
          <DeviceDetailRow label="Trạng thái" value={renderDeviceStatus(detailRecord?.status)} />
          <DeviceDetailRow label="Số serie" value={detailRecord?.serial_no} />
          <DeviceDetailRow label="Số IMEI" value={detailRecord?.imei} />
          <DeviceDetailRow label="Số IMSI" value={detailRecord?.imsi} />
          <DeviceDetailRow label="Model" value={detailRecord?.model} />
          <DeviceDetailRow label="Nhà mạng" value={detailRecord?.operator} />
          <DeviceDetailRow
            label="Tình trạng thiết bị"
            value={renderDeviceCondition(detailRecord)}
          />
          <DeviceDetailRow label="Ghi chú" value={detailRecord?.state_note} />
        </div>
      </Modal>
      {/* Modal cập nhật tình trạng thiết bị */}
      <Modal
        title="Cập nhật tình trạng thiết bị"
        open={conditionModalVisible}
        onCancel={() => setConditionModalVisible(false)}
        onOk={handleSaveCondition}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={isSavingCondition}
        destroyOnClose>
        <Form layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item label="Thiết bị" style={{ marginBottom: 12 }}>
            <Input value={conditionRecord?.serial_no} disabled />
          </Form.Item>
          <Form.Item label="Loại tình trạng" required style={{ marginBottom: 12 }}>
            <Select
              placeholder="Chọn loại tình trạng"
              value={conditionCode}
              onChange={(val) => {
                setConditionCode(val);
                if (val !== 'OTHER') setConditionName('');
              }}
              style={{ width: '100%' }}>
              {DEVICE_CONDITIONS.map((c) => (
                <Select.Option key={c.code} value={c.code}>
                  <Tag color={c.color} style={{ marginRight: 8 }}>
                    {c.name}
                  </Tag>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {conditionCode === 'OTHER' && (
            <Form.Item label="Mô tả tình trạng" required>
              <Input
                placeholder="Nhập tên/mô tả tình trạng thiết bị..."
                value={conditionName}
                onChange={(e) => setConditionName(e.target.value)}
                maxLength={200}
                showCount
              />
            </Form.Item>
          )}
          <Form.Item label="Ghi chú" style={{ marginBottom: 0 }}>
            <Input.TextArea
              placeholder="Nhập ghi chú..."
              value={conditionNote}
              onChange={(e) => setConditionNote(e.target.value)}
              maxLength={500}
              showCount
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4}>Quản lý thiết bị</Typography.Title>
        </Col>
        <Col>
          <Space>
            {device.import && (
              <Button icon={<UploadOutlined />} onClick={() => setVisibleModalImport(true)}>
                Nhập file excel
              </Button>
            )}
            {device.export && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportExcelButtonClick}
                loading={loading}>
                Xuất file excel
              </Button>
            )}
            {device.create && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddDevice}>
                Thêm mới
              </Button>
            )}
          </Space>
        </Col>
      </Row>
      <Card bodyStyle={{ padding: '8px' }}>
        <Row gutter={[8, 8]} style={{ marginBottom: '8px' }}>
          <Col md={6} xs={12}>
            <Button
              onClick={() => setType(type === 'LD' ? '' : 'LD')}
              style={{
                backgroundColor: type === 'LD' ? '#0284c7' : '#eef1f6',
                borderColor: type === 'LD' ? '#0284c7' : '#eef1f6',
                color: type === 'LD' ? '#fff' : '#0284c7',
                height: '36px',
                width: '100%'
              }}>
              {type === 'LD' ? 'Thiết bị kiểm tra rò rỉ' : 'Thiết bị thông thường'}
            </Button>
          </Col>
        </Row>
        <Row gutter={[8, 8]}>
          {/* <Col md={6} xs={12}>
            <SelectCommunity value={selectedCommunity} onChange={setSelectedCommunity} />
          </Col> */}
          <Col md={6} xs={12}>
            <Input
              value={identifierSearch}
              onChange={(event) => setIdentifierSearch(event.target.value)}
              onPressEnter={handleSearch}
              placeholder="Tìm theo serial, IMEI, IMSI..."
              style={{ width: '100%', height: '36px' }}
            />
          </Col>
          <Col md={6} xs={12}>
            <Input
              value={modelSearch}
              onChange={(event) => setModelSearch(event.target.value)}
              onPressEnter={handleSearch}
              placeholder="Tìm theo model..."
              style={{ width: '100%', height: '36px' }}
            />
          </Col>
          <Col md={4} xs={12}>
            <Select
              allowClear
              placeholder="Tình trạng thiết bị"
              value={stateFilter}
              onChange={(val) => {
                setStateFilter(val ?? null);
                setCurrentPage(1);
              }}
              style={{ width: '100%', height: '36px' }}>
              {DEVICE_CONDITIONS.map((c) => (
                <Select.Option key={c.code} value={c.code}>
                  <Tag color={c.color} style={{ marginRight: 4 }}>
                    {c.name}
                  </Tag>
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Button
              onClick={handleSearch}
              icon={<SearchOutlined />}
              style={{
                backgroundColor: '#0284c7',
                borderColor: '#0284c7',
                color: '#fff',
                height: '36px'
              }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>
      </Card>
      <Card bodyStyle={{ padding: 0 }}>
        <Tabs
          activeKey={status}
          defaultActiveKey="all"
          onChange={(value) => {
            setCurrentPage(1);
            setStatus(value);
          }}>
          <TabPane tab={`Tất cả`} key={'all'}>
            <Table
              bordered
              // size="middle"
              loading={isFetching}
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Đang sử dụng`} key={'IN_USE'}>
            <Table
              bordered
              // size="middle"
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Lưu kho`} key={'STORED'}>
            <Table
              bordered
              // size="middle"
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Bảo hành`} key={'UNDER_WARRANTY'}>
            <Table
              bordered
              // size="middle"
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
          <TabPane tab={`Không dùng được`} key={'UNUSABLE'}>
            <Table
              bordered
              // size="middle"
              dataSource={deviceList || []}
              columns={columns}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                onChange: handleChangePage,
                onShowSizeChange: handleChangePageSize,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
              }}
              style={{
                borderRadius: 8,
                thead: { backgroundColor: '#e2e8f0', color: '#64748b' },
                fontSize: '30px'
              }}
              scroll={{ x: 'max-content', y: 600 }}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
}
